import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  Divider,
  Loader,
  Center,
  Select,
  Textarea,
  TextInput,
  Modal,
  Breadcrumbs,
  Anchor,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { itemsApi } from '../api/items';
import { categoriesApi } from '../api/categories';
import { buildCategorySelectData } from '../utils/categories';
import { ITEM_STATUS_LABELS, ITEM_STATUS_COLORS } from '../constants/status';
import { ImageUpload } from '../components/ImageUpload';
import type { ItemStatus, CreateItemInput } from '../types';
import { pageContainer } from '../styles/theme.css';
import { getSemester, daysAgoLabel } from '../utils/semester';


export function ItemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const itemId = Number(id);
  const [editing, setEditing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const {
    data: item,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['items', itemId],
    queryFn: () => itemsApi.get(itemId),
    enabled: !isNaN(itemId),
  });

  const { data: catData } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.get,
    staleTime: Infinity,
  });

  const categorySelectData = buildCategorySelectData(catData);

  const form = useForm<Partial<CreateItemInput>>({
    initialValues: {},
  });

  function startEditing() {
    if (!item) return;
    form.setValues({
      title: item.title,
      category_id: item.category_id,
      color: item.color ?? '',
      brand: item.brand ?? '',
      distinctive_features: item.distinctive_features ?? '',
      found_date: item.found_date,
      discard_after: item.discard_after ?? '',
      storage_location: item.storage_location ?? '',
      notes: item.notes ?? '',
    });
    setEditing(true);
  }

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateItemInput> & { status?: ItemStatus }) =>
      itemsApi.update(itemId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
      notifications.show({ message: 'Genstand opdateret', color: 'green' });
      setEditing(false);
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => itemsApi.delete(itemId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
      notifications.show({ message: 'Genstand slettet', color: 'orange' });
      navigate('/');
    },
  });

  const addImageMutation = useMutation({
    mutationFn: ({ url, ziplineId }: { url: string; ziplineId?: string }) =>
      itemsApi.addImage(itemId, url, ziplineId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items', itemId] }),
  });

  const deleteImageMutation = useMutation({
    mutationFn: (imageId: number) => itemsApi.deleteImage(itemId, imageId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items', itemId] }),
  });

  if (isLoading)
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  if (error || !item)
    return (
      <Text c="red" p="md">
        Genstand ikke fundet.
      </Text>
    );

  return (
    <div className={pageContainer}>
      <Stack gap="md">
        <Breadcrumbs>
          <Anchor onClick={() => navigate('/')} size="sm">
            Oversigt
          </Anchor>
          <Text size="sm">{item.title}</Text>
        </Breadcrumbs>

        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Title order={2}>{item.title}</Title>
            <Group gap="xs">
              <Badge color={ITEM_STATUS_COLORS[item.status]} variant="light">
                {ITEM_STATUS_LABELS[item.status]}
              </Badge>
              <Text size="sm" c="dimmed">
                {catData?.categories.find((c) => c.id === item.category_id)?.label_da ?? item.category_id}
              </Text>
            </Group>
          </Stack>

          <Group gap="xs">
            {!editing && (
              <Button variant="default" size="xs" onClick={startEditing}>
                Rediger
              </Button>
            )}
            <Button variant="light" color="red" size="xs" onClick={() => setDeleteModal(true)}>
              Slet
            </Button>
          </Group>
        </Group>

        {/* Status change */}
        {!editing && item.status === 'available' && (
          <Group gap="xs">
            <Button
              size="xs"
              color="blue"
              variant="light"
              onClick={() => updateMutation.mutate({ status: 'returned' })}
              loading={updateMutation.isPending}
            >
              Marker som udleveret
            </Button>
            <Button
              size="xs"
              color="red"
              variant="light"
              onClick={() => updateMutation.mutate({ status: 'discarded' })}
              loading={updateMutation.isPending}
            >
              Marker som kasseret
            </Button>
          </Group>
        )}

        <Divider />

        {/* Edit form or read-only details */}
        {editing ? (
          <form onSubmit={form.onSubmit((v) => updateMutation.mutate(v))}>
            <Stack gap="sm">
              <TextInput label="Titel" required {...form.getInputProps('title')} />
              <Select
                label="Kategori"
                searchable
                data={categorySelectData}
                {...form.getInputProps('category_id')}
              />
              <Group grow>
                <TextInput label="Farve" {...form.getInputProps('color')} />
                <TextInput label="Mærke" {...form.getInputProps('brand')} />
              </Group>
              <TextInput label="Fundet dato" type="date" {...form.getInputProps('found_date')} />
              <TextInput
                label="Kassér efter"
                type="date"
                {...form.getInputProps('discard_after')}
              />
              <Select
                label="Opbevaringssted"
                data={[
                  { label: 'Fjernlager', value: 'Fjernlager' },
                  { label: 'Glemte sager kasse', value: 'Glemte sager kasse' },
                ]}
                {...form.getInputProps('storage_location')}
              />
              <Textarea label="Særlige kendetegn" {...form.getInputProps('distinctive_features')} />
              <Textarea label="Noter" {...form.getInputProps('notes')} />
              <Group>
                <Button type="submit" loading={updateMutation.isPending}>
                  Gem
                </Button>
                <Button variant="default" onClick={() => setEditing(false)}>
                  Annuller
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <Stack gap="xs">
            <Text size="sm">
              <b>Kategori:</b>{' '}
              {catData?.categories.find((c) => c.id === item.category_id)?.label_da ?? item.category_id}
            </Text>
            {item.color && (
              <Text size="sm">
                <b>Farve:</b> {item.color}
              </Text>
            )}
            {item.brand && (
              <Text size="sm">
                <b>Mærke:</b> {item.brand}
              </Text>
            )}
            {item.distinctive_features && (
              <Text size="sm">
                <b>Særlige kendetegn:</b> {item.distinctive_features}
              </Text>
            )}
            <Group gap="xs" align="center">
              <Text size="sm">
                <b>Fundet:</b> {new Date(item.found_date + 'T12:00:00').toLocaleDateString('da-DK')}
              </Text>
              <Badge size="xs" variant="light" color="blue">
                {getSemester(item.found_date)}
              </Badge>
              <Text size="xs" c="dimmed">{daysAgoLabel(item.found_date)}</Text>
            </Group>
            {item.discard_after && (
              <Text size="sm">
                <b>Kassér efter:</b> {new Date(item.discard_after).toLocaleDateString('da-DK')}
              </Text>
            )}
            {item.storage_location && (
              <Text size="sm">
                <b>Opbevaring:</b> {item.storage_location}
              </Text>
            )}
            {item.notes && (
              <Text size="sm">
                <b>Noter:</b> {item.notes}
              </Text>
            )}
          </Stack>
        )}

        <Divider label="Billeder" />

        <ImageUpload
          existingImages={item.images}
          onUpload={(url, ziplineId) => addImageMutation.mutate({ url, ziplineId })}
          onDelete={(imageId) => deleteImageMutation.mutate(imageId)}
        />
      </Stack>

      {/* Delete confirmation */}
      <Modal
        opened={deleteModal}
        onClose={() => setDeleteModal(false)}
        title="Slet genstand"
        centered
      >
        <Text size="sm">
          Er du sikker på, at du vil slette <b>{item.title}</b>?
        </Text>
        <Group mt="md" justify="flex-end">
          <Button variant="default" onClick={() => setDeleteModal(false)}>
            Annuller
          </Button>
          <Button
            color="red"
            loading={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            Slet
          </Button>
        </Group>
      </Modal>
    </div>
  );
}
